/** @jsx h **/
import { ref, watch } from "vue";
import { debounce } from "@gratico/stdlib";
import { IViewportKernel } from "@gratico/sdk";
import { onMounted, onUnmounted } from "vue";
import animate, { AnimeInstance } from "animejs";

export function useDockState(kernel: IViewportKernel, configKey: string) {
  const docked = ref<boolean>(!!kernel.userConfig.get(configKey));

  watch(docked, (val) => {
    kernel.userConfig.set(configKey, val);
  });

  return { docked };
}

export function getParents(a: ParentNode | null) {
  const els = [];
  while (a) {
    els.unshift(a);
    a = a.parentNode;
  }
  return els;
}

export function useSliding(
  docked?: ReturnType<typeof ref<boolean>>,
  direction: -1 | 1 = 1
) {
  const expanded = ref<boolean | undefined>(undefined);
  const sidebarRef = ref<HTMLElement | null>(null);
  let animation: AnimeInstance | undefined;

  const handleSlideAnimation = async () => {
    const sidebar = sidebarRef.value;
    if (!sidebar) return;

    const isExpanded = expanded.value;
    const isDocked = docked?.value;
    const translateX = (isDocked ? 0 : isExpanded ? 0 : 90) * direction;

    if (animation && animation.remaining > 1) {
      await animation.finished;
    }

    animation = animate({
      targets: sidebar,
      translateX: `${translateX}%`,
      easing: "spring(1, 100, 6, 0)",
    });

    await animation.finished;
    animation = undefined;
  };

  const handleMouseMove = debounce((e: MouseEvent) => {
    const path = getParents(e.target as HTMLElement);
    if (sidebarRef.value && path.includes(sidebarRef.value)) {
      expanded.value = true;
    } else {
      expanded.value = false;
    }
  }, 100);

  onMounted(() => {
    document.addEventListener("mousemove", handleMouseMove);
  });

  onUnmounted(() => {
    document.removeEventListener("mousemove", handleMouseMove);
  });

  watch(expanded, () => {
    handleSlideAnimation();
  });

  const mouseEvent = debounce((state: boolean) => {
    if (docked?.value) {
      expanded.value = true;
    } else {
      expanded.value = state;
    }
  }, 100);

  return {
    mouseEvent,
    expanded,
    handleMouseMove,
    sidebarRef,
  };
}
